import type { ReactNode } from 'react'
import { LineTabs } from '@/components/carbon/LineTabs'

// TODO: When Slice A-state lands (`hooks/useAdminPortalState.ts`), import this
// type from there and remove the local declaration:
//   import type { AdminTab } from '../../hooks/useAdminPortalState'
export type AdminTab = 'organizations' | 'people' | 'tenants'

interface AdminPillTabsProps {
  activeTab: AdminTab
  onTabChange: (tab: AdminTab) => void
  /**
   * Pending items per tab, drawn as a count tag. 0 hides the tag, and so
   * does undefined or null (a count that has not loaded or did not load is
   * unknown, never a fabricated 0).
   */
  counts: { organizations?: number | null; people?: number | null; tenants?: number | null }
  primaryCTA?: ReactNode
}

const TABS: ReadonlyArray<{ key: AdminTab; label: string }> = [
  { key: 'organizations', label: 'Organizations' },
  { key: 'people', label: 'People' },
  { key: 'tenants', label: 'Monitored Tenants' },
]

/** Tab ids are `admin-tab-{key}`; each controls the `admin-panel-{key}` region AdminPortal renders. */
export const ADMIN_TAB_ID_PREFIX = 'admin-tab-'
export const ADMIN_PANEL_ID_PREFIX = 'admin-panel-'

/**
 * The /admin portal's sections as Carbon line tabs (page sub-navigation):
 * 48px items over a 2px border-subtle baseline, the selected one 600 with
 * the interactive underline, a pending count as a tag. The strip scrolls on
 * its own on a phone, so it never widens the page.
 */
export function AdminPillTabs({ activeTab, onTabChange, counts, primaryCTA }: AdminPillTabsProps) {
  return (
    <LineTabs
      aria-label="Admin portal sections"
      value={activeTab}
      onChange={(id) => onTabChange(id as AdminTab)}
      tabIdPrefix={ADMIN_TAB_ID_PREFIX}
      panelIdPrefix={ADMIN_PANEL_ID_PREFIX}
      actions={primaryCTA}
      items={TABS.map(({ key, label }) => {
        const count = counts[key]
        return { id: key, label, count: typeof count === 'number' && count > 0 ? count : null }
      })}
    />
  )
}

export default AdminPillTabs
